// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a step's effects over the live globe (docs/AYNI_XR_STUDIO_MOTION_PLAN.md):
 * the same painter as the render (`services/studio/effects.ts`) on a canvas
 * laid over the map, for the Khipu player and the Studio's preview.
 *
 * Live there is no clean frame to copy, so what a render makes from one is
 * done by the browser behind the canvas: the depth blur and a title card's
 * blur with a blurred backdrop masked to the edges, the spotlight's grey with
 * a greyed backdrop masked to everything outside the circle. The data inside
 * the focus stays untouched here too. The canvas never takes a click.
 */

import { paintEffects, spotlightCircle, fade, type EffectsClock } from '../services/studio/effects'
import type { KhipuEffects } from '../services/khipu'

export interface EffectsOverlayHost {
  /** The element the globe draws in (the overlay covers it). */
  container(): HTMLElement | null
  project(lat: number, lon: number): { x: number; y: number } | null
  readValue(lat: number, lon: number): string | null
  language(): string
  accent?: string
  /** A split compare drawn on the globe now: the dates either side of its line, or null. */
  compareLabels?(effects: KhipuEffects): { here: string; there: string } | null
}

export interface EffectsOverlay {
  /** Paint the effects at this moment of the step (call every animation frame while they show). */
  paint(effects: KhipuEffects, clock: EffectsClock): void
  clear(): void
  destroy(): void
}

const FONT = '"Inter", "Segoe UI", system-ui, sans-serif'

export function createEffectsOverlay(host: EffectsOverlayHost): EffectsOverlay {
  let layer: HTMLDivElement | null = null
  let canvas: HTMLCanvasElement | null = null
  let blur: HTMLDivElement | null = null
  let grey: HTMLDivElement | null = null

  function ensure(): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
    const box = host.container()
    if (!box) return null
    if (!layer || !canvas || layer.parentElement !== box) {
      layer?.remove()
      // One layer at the globe's own level: the app's panels and date stay above it, sharp.
      layer = document.createElement('div')
      layer.className = 'effects-overlay-layer'
      layer.setAttribute('aria-hidden', 'true')
      const backdrop = (cls: string) => {
        const d = document.createElement('div')
        d.className = `effects-overlay-backdrop ${cls}`
        layer!.appendChild(d)
        return d
      }
      blur = backdrop('effects-overlay-blur')
      grey = backdrop('effects-overlay-grey')
      canvas = document.createElement('canvas')
      canvas.className = 'effects-overlay'
      layer.appendChild(canvas)
      box.appendChild(layer)
    }
    const w = box.clientWidth, h = box.clientHeight
    const dpr = window.devicePixelRatio || 1
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    return { ctx, w, h }
  }

  function setBackdrop(el: HTMLDivElement | null, filter: string | null, mask: string | null, opacity: number): void {
    if (!el) return
    if (!filter || opacity <= 0) { el.style.display = 'none'; return }
    el.style.display = 'block'
    el.style.backdropFilter = filter
    el.style.setProperty('-webkit-backdrop-filter', filter)
    el.style.maskImage = mask ?? 'none'
    el.style.setProperty('-webkit-mask-image', mask ?? 'none')
    el.style.opacity = String(opacity)
  }

  return {
    paint(effects, clock) {
      const got = ensure()
      if (!got) return
      const { ctx, w, h } = got
      ctx.clearRect(0, 0, w, h)
      const alpha = fade(clock)
      const card = effects.titleCard
      const cardAlpha = card ? fade({ local: clock.local, hold: Math.min(card.seconds ?? 2.5, clock.hold) }, 0.35, 0.45) : 0
      // Blur: the whole picture under a title card; else the edges for a depth blur (a sharp centre kept).
      if (cardAlpha > 0) setBackdrop(blur, 'blur(14px)', null, cardAlpha)
      else if (effects.depthBlur && effects.depthBlur > 0) {
        const r0 = Math.min(w, h) * 0.3, r1 = Math.min(w, h) * 0.62
        setBackdrop(blur, `blur(${Math.round(2 + 10 * effects.depthBlur)}px)`, `radial-gradient(circle at 50% 50%, transparent ${r0}px, black ${r1}px)`, alpha)
      } else setBackdrop(blur, null, null, 0)
      const spot = effects.spotlight?.desaturate ? spotlightCircle(effects.spotlight, host) : null
      setBackdrop(grey, spot ? 'grayscale(1)' : null, spot ? `radial-gradient(circle at ${spot.x}px ${spot.y}px, transparent ${spot.r}px, black ${spot.r + 1}px)` : null, alpha)
      paintEffects(ctx, effects, clock, {
        width: w, height: h, project: host.project, readValue: host.readValue, source: null,
        language: host.language(), accent: host.accent ?? '#c0392b', font: FONT, counterAlign: 'centre',
        compareLabels: effects.compare ? host.compareLabels?.(effects) ?? null : null,
      })
    },
    clear() {
      if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
      setBackdrop(blur, null, null, 0)
      setBackdrop(grey, null, null, 0)
    },
    destroy() {
      layer?.remove()
      layer = null; canvas = null; blur = null; grey = null
    },
  }
}
